<ng-container *transloco="let t">
  <div class="flex h-full flex-col items-center justify-center gap-6">
    <div class="space-y-2 text-center">
      <h1 class="text-4xl font-bold md:text-5xl">
        {{ t('edit-profile-form-title') }}
      </h1>
      <p>
        {{ t('edit-profile-form-subtitle') }}
      </p>
    </div>
    <div class="card bg-base-100 w-full max-w-xl shadow-2xl">
      <div class="card-body">
        <div class="card-title">
          <core-avatar
            [hasUploadButton]="true"
            class="mx-auto"
            circleClasses="w-36 h-36"
            textClasses="text-3xl"
          />
        </div>
        <form [formGroup]="userForm">
          <div class="space-y-4">
            <select
              (change)="selectTitle($event)"
              class="btn-ghost select select-bordered w-fit py-1 text-sm"
              formControlName="userFormTitle"
            >
              <option [selected]="!selectedTitle" [value]="''" disabled>
                Choose Title
              </option>
              @for (title of titles; track title) {
                <option [selected]="title === selectedTitle" [value]="title">
                  {{ title }}
                </option>
              }
            </select>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">{{
                  t('edit-profile-form-first-name')
                }}</span>
                <input
                  [attr.aria-invalid]="
                    userFormFirstName?.invalid && userFormFirstName?.touched
                      ? true
                      : null
                  "
                  [required]="true"
                  class="input input-bordered w-full grow"
                  formControlName="userFormFirstName"
                  [ngClass]="{
                    'input-error':
                      userFormFirstName?.invalid && userFormFirstName?.touched,
                  }"
                  placeholder="First Name"
                  type="text"
                />
                @if (userFormFirstName?.invalid && userFormFirstName?.touched) {
                  @if (
                    userFormFirstName?.errors?.['minlength'] ||
                    userFormFirstName?.errors?.['maxlength']
                  ) {
                    <span class="label-text-alt text-error">
                      {{
                        t('edit-profile-error-first-name-minMaxLength', {
                          minLength: 2,
                          maxLength: 64,
                        })
                      }}
                    </span>
                  }
                }
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">{{
                  t('edit-profile-form-last-name')
                }}</span>
                <input
                  class="input input-bordered w-full grow"
                  [required]="true"
                  formControlName="userFormLastName"
                  [ngClass]="{
                    'input-error':
                      userFormLastName?.invalid && userFormLastName?.touched,
                  }"
                  placeholder="Last Name"
                  type="text"
                />
                @if (userFormLastName?.invalid && userFormLastName?.touched) {
                  @if (
                    userFormLastName?.errors?.['minlength'] ||
                    userFormLastName?.errors?.['maxlength']
                  ) {
                    <span class="label-text-alt text-error">
                      {{
                        t('edit-profile-error-last-name-minMaxLength', {
                          minLength: 2,
                          maxLength: 64,
                        })
                      }}
                    </span>
                  }
                }
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">{{
                  t('edit-profile-form-email')
                }}</span>
                <input
                  [attr.aria-invalid]="
                    userFormEmail?.invalid && userFormEmail?.touched
                      ? true
                      : null
                  "
                  [required]="true"
                  class="input input-bordered w-full grow"
                  formControlName="userFormEmail"
                  [ngClass]="{
                    'input-error':
                      userFormEmail?.invalid && userFormEmail?.touched,
                  }"
                  placeholder="Email"
                  type="text"
                />
                @if (userFormEmail?.invalid && userFormEmail?.touched) {
                  @if (userFormEmail?.errors?.['email']) {
                    <p class="label-text-alt text-error">
                      {{ t('edit-profile-error-email-format') }}
                    </p>
                  }
                }
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">
                  {{ t('register-user-form-password') }}
                </span>
                <div class="relative w-full">
                  <input
                    [attr.aria-invalid]="
                      userFormPassword?.invalid && userFormPassword?.touched
                        ? true
                        : null
                    "
                    [type]="getInputType()"
                    autocomplete="on"
                    class="input input-bordered w-full pr-16"
                    formControlName="userFormPassword"
                    [ngClass]="{
                      'input-error':
                        userFormPassword?.invalid && userFormPassword?.touched,
                    }"
                    placeholder="{{ t('edit-user-dialog-form-password') }}"
                  />
                  <button
                    (click)="toggleShowPassword()"
                    class="btn btn-square btn-ghost absolute right-0"
                    type="button"
                  >
                    <svg
                      class="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      @if (showPassword) {
                        <ng-container>
                          <path
                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </ng-container>
                      } @else {
                        <ng-template *ngTemplateOutlet="EyeIcon"></ng-template>
                      }
                      <ng-template #EyeIcon>
                        <path
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </ng-template>
                    </svg>
                  </button>
                  @if (userFormPassword?.invalid && userFormPassword?.touched) {
                    @if (userFormPassword?.errors?.['pattern']) {
                      <p class="label-text-alt text-error">
                        {{ t('register-user-form-error-password-pattern') }}
                      </p>
                    }
                  }
                </div>
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">
                  {{ t('register-user-form-confirm-password') }}
                </span>
                <div class="relative w-full">
                  <input
                    [attr.aria-invalid]="
                      userFormConfirmPassword?.invalid &&
                      userFormConfirmPassword?.touched
                        ? true
                        : null
                    "
                    [type]="getInputType()"
                    autocomplete="on"
                    class="input input-bordered w-full pr-16"
                    formControlName="userFormConfirmPassword"
                    [ngClass]="{
                      'input-error':
                        userFormConfirmPassword?.invalid &&
                        userFormConfirmPassword?.touched,
                    }"
                    placeholder="{{ t('register-user-form-confirm-password') }}"
                  />
                  @if (
                    userFormConfirmPassword?.invalid &&
                    userFormConfirmPassword?.touched
                  ) {
                    @if (userFormConfirmPassword?.errors?.['pattern']) {
                      <p class="label-text-alt text-error">
                        {{
                          t('register-user-form-error-confirm-password-pattern')
                        }}
                      </p>
                    }
                    @if (
                      userFormConfirmPassword?.errors?.['passwordsMismatch']
                    ) {
                      <p class="label-text-alt text-error">
                        {{
                          t(
                            'register-user-form-error-confirm-password-mismatch'
                          )
                        }}
                      </p>
                    }
                  }
                </div>
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">{{
                  t('edit-profile-form-company-name')
                }}</span>
                <input
                  [attr.aria-invalid]="
                    userFormCompanyName?.invalid && userFormCompanyName?.touched
                      ? true
                      : null
                  "
                  [required]="true"
                  class="input input-bordered w-full grow"
                  formControlName="userFormCompanyName"
                  [ngClass]="{
                    'input-error':
                      userFormCompanyName?.invalid &&
                      userFormCompanyName?.touched,
                  }"
                  placeholder="Company Name"
                  type="text"
                />
              </label>
            </div>
            <div>
              <label class="max-w-xs-100 w-full">
                <span class="label-text">{{
                  t('edit-profile-form-business-unit')
                }}</span>
                <input
                  class="input input-bordered w-full grow"
                  formControlName="userFormBusinessUnit"
                  placeholder="Business Unit"
                  type="text"
                />
              </label>
            </div>

            <!-- country select -->

            <div>
              <label class="w-full max-w-xl" for="profile-country-dropdown">
                <span class="label-text">{{
                  t('register-user-form-country')
                }}</span>
                @if (countryService.getCountriesReady() | async) {
                  <select
                    id="profile-country-dropdown"
                    class="btn-ghost select select-bordered flex w-full grow py-1 text-sm"
                    formControlName="userFormCountry"
                    [ngClass]="{
                      'select-error':
                        userFormCountry?.invalid && userFormCountry?.touched,
                    }"
                  >
                    @for (
                      country of countryService.getCountryList();
                      track country
                    ) {
                      <option [value]="country.alpha_2">
                        {{ country.name }}
                      </option>
                    }
                  </select>
                }
              </label>
            </div>

            <button
              (click)="updateProfile()"
              [disabled]="userForm.invalid"
              class="btn btn-primary mt-4 w-full"
            >
              {{ t('edit-profile-form-save-button') }}
            </button>
            @if ((emailTakenError$$ | async) || (genericError$$ | async)) {
              <div
                class="alert alert-error mt-4 flex items-center justify-center text-center"
                [ngClass]="{
                  'opacity-100': showAlert,
                  'opacity-0': !showAlert,
                  'transition-opacity': true,
                  'duration-1000': true,
                  'ease-in-out': true,
                }"
                role="alert"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 shrink-0 stroke-current"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                @if (emailTakenError$$ | async) {
                  <span class="mx-auto">
                    {{ t('edit-profile-error-email-taken') }}
                  </span>
                }
                @if (genericError$$ | async) {
                  <span class="mx-auto">
                    {{ t('edit-profile-error-generic-error-message') }}
                  </span>
                }
              </div>
            }
            @if (updateSuccess$$ | async) {
              <div
                [ngClass]="{
                  'opacity-100': showAlert,
                  'opacity-0': !showAlert,
                  'transition-opacity': true,
                  'duration-1000': true,
                  'ease-in-out': true,
                }"
                class="alert alert-success mt-4 flex items-center justify-center text-center"
                role="alert"
              >
                <svg
                  class="h-6 w-6 shrink-0 stroke-current"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
                <span>{{ t('edit-profile-update-success-alert') }}</span>
              </div>
            }
          </div>
        </form>
      </div>
    </div>
  </div>
</ng-container>
